import { motion } from "framer-motion";
import { useEffect, useState } from "react";

interface Project {
  id: number;
  tytul: string;
  zdjecie: string;
  czas: number;
  jestDuzy?: boolean;
}

const getOptimizedUrl = (url: string, width: number, isLarge: boolean) => {
  if (url.includes("res.cloudinary.com/daihsegip")) {
    const [baseUrl, imagePath] = url.split("upload/");

    const transformations = [
      "f_webp",
      "q_auto:best",
      `w_${isLarge ? 1920 : 960}`,
      "c_scale",
      isLarge ? "h_1080" : "h_540",
    ].join(",");

    return `${baseUrl}upload/${transformations}/${imagePath}`;
  }
  return url;
};

const ProjectCard = ({
  project,
  isLarge,
}: {
  project: Project;
  isLarge: boolean;
}) => {
  const imageWidth = isLarge ? 1920 : 960;
  const optimizedUrl = getOptimizedUrl(project.zdjecie, imageWidth, isLarge);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className={`relative group ${isLarge ? "md:col-span-2 col-span-1" : ""}`}
    >
      <div className="aspect-[16/9] w-full">
        <img
          src={optimizedUrl}
          alt={project.tytul}
          className="w-full h-full object-cover shadow-xl"
          loading="lazy"
          sizes={
            isLarge
              ? "(max-width: 1920px) 100vw, 1920px"
              : "(max-width: 960px) 100vw, 960px"
          }
          onError={(e) => {
            // Fallback if optimization fails
            const target = e.target as HTMLImageElement;
            target.src = project.zdjecie;
          }}
        />
        <div className="sm:text-4xl">
          <div className="shadow-lg absolute inset-0 bg-gradient-to-t from-black via-30% via-transparent to-transparent opacity-0 group-hover:opacity-70 transition-opacity duration-700" />
          <div className="shadow-lg absolute inset-0 flex items-end justify-start p-8 opacity-0 group-hover:opacity-100 transition-opacity duration-600 group-hover:delay-200">
            <h2 className="text-white lg:text-4xl md:text-2xl font-bold">
              {project.tytul}
            </h2>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const WorkGrid = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log("Fetching images...");
    fetch("/data/images.json")
      .then((response) => {
        if (!response.ok) throw new Error("Failed to fetch images");
        return response.json();
      })
      .then((data) => {
        if (!Array.isArray(data)) {
          throw new Error("Data is not an array");
        }
        setProjects(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error loading images:", error);
        setError(error.message);
        setProjects([]);
        setLoading(false);
      });
  }, []);

  // Update scroll handler to prevent URL change
  const scrollToWork = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault(); // This prevents the default anchor behavior
    const workSection = document.getElementById("Work");
    if (workSection) {
      workSection.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        Error loading images: {error}
      </div>
    );
  }

  return (
    <section id="Work" className="px-4 md:px-4 lg:px-4">
      <a onClick={scrollToWork} className="cursor-pointer"></a>
      <div className="grid md:grid-cols-2 grid-cols-1 gap-5 max-w-[1920px] mx-auto">
        {projects.length === 0 ? (
          <div className="col-span-2 text-center py-8">
            Nie znaleziono zdjec
          </div>
        ) : (
          projects.map((project, index) => (
            <ProjectCard
              key={project.id}
              project={project}
              isLarge={project.jestDuzy ?? index % 3 === 0}
            />
          ))
        )}
      </div>
    </section>
  );
};

export default WorkGrid;
